import { AxiosResponse } from "axios";
import { IPaginatorPageChange } from "dots-ui/dotsPaginator";
import { IColumnConfig } from "dots-ui/dotsTable";
import { FormikProps } from "formik";
import { isEqual } from "lodash";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnyObject, ObjectSchema } from "yup";
import { AvisConstants } from "../../../../../data/avisConstants";
import { useSessionStorage } from "../../../../../hooks/useSessionStorage";
import {
  IColumnConfigConditional,
  IFilterProps,
  IPaginatedList,
} from "../../../../../models";
import { IAxiosError } from "../../../../../utils";
import { errorDataLoading } from "../../../appStates/appStates";
import { AvisFilter } from "../../form/filter/AvisFilter";
import { AvisFilterableTableGrid } from "./AvisFilterableTableGrid";

interface IAvisFilterableTableProps<T, F extends IFilterProps> {
  defaultFilterValues: F;
  filterSessionKey?: string;
  filterValidationSchema: ObjectSchema<F>;
  tableColumns: IColumnConfig[];
  tableColumnWidths?: number[];
  conditionalFormatting?: IColumnConfigConditional<T>[];
  setError: React.Dispatch<React.SetStateAction<IAxiosError | undefined>>;
  loadDataOnInit?: boolean;
  getApiData: (
    filter: F,
  ) => Promise<AxiosResponse<IPaginatedList<T>, any>> | undefined;
  renderFilter: (
    formikProps: FormikProps<F>,
    loading: boolean,
    filtered: boolean,
  ) => React.ReactNode;
  forceUpdateKey?: any;
  customRowClassNames?: (rowData: T) => string;
  renderFooter?: (hasData: boolean) => ReactNode;
}
export function AvisFilterableTable<T extends object, F extends AnyObject>(
  props: Readonly<IAvisFilterableTableProps<T, F>>,
) {
  const {
    defaultFilterValues,
    filterSessionKey,
    filterValidationSchema,
    tableColumnWidths,
    tableColumns,
    conditionalFormatting,
    forceUpdateKey,
    loadDataOnInit = true,
    setError,
    getApiData,
    renderFilter,
    customRowClassNames,
    renderFooter,
  } = props;

  const [filterStored, setFilterStored] = useSessionStorage(
    filterSessionKey
      ? `${filterSessionKey}${AvisConstants.grid_filter_suffix}`
      : undefined,
  );
  const [lastDataset, setLastDataset] = useSessionStorage(
    filterSessionKey
      ? `${filterSessionKey}${AvisConstants.grid_data_suffix}`
      : undefined,
  );
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [paginatedGridInfo, setPaginatedGridInfo] =
    useState<IPaginatedList<T>>(lastDataset);

  const [filter, setFilter] = useState<F>(
    hash === AvisConstants.url_hash_filter && filterStored
      ? filterStored
      : defaultFilterValues,
  );
  const [firstPageLoad, setFirstPageLoad] = useState<boolean>(true);
  const [triggerRefresh, setTriggerRefresh] = useState<boolean>(loadDataOnInit);
  const [isGridLoading, setIsGridLoading] = useState<boolean>(
    !lastDataset && loadDataOnInit,
  );
  const [forceUpdate, setForceUpdate] = useState<any>();

  const getGridData = useMemo(() => {
    if (!triggerRefresh || errorDataLoading.value) return;

    setTriggerRefresh(false);
    setIsGridLoading(true);

    setFilterStored(filter);

    const apiPromise = getApiData(filter);

    if (!apiPromise) return;

    apiPromise
      .then((res) => {
        setLastDataset(res.data);
        setPaginatedGridInfo(res.data);
      })
      .catch(setError)
      .finally(() => {
        setIsGridLoading(false);
        if (filterSessionKey) {
          navigate(AvisConstants.url_hash_filter, {
            replace: true,
          });
        }
      });
  }, [
    filter,
    triggerRefresh,
    navigate,
    filterSessionKey,
    setFilterStored,
    setLastDataset,
    getApiData,
    setError,
  ]);
  useEffect(() => {
    //Workaraund for checkbox template rendering problem
    //Allows to rerender filtrable datatable from outside on selection change
    if (forceUpdate !== forceUpdateKey) {
      setForceUpdate(forceUpdateKey);
    }
  }, [forceUpdateKey, forceUpdate]);

  useEffect(() => {
    if (!loadDataOnInit && firstPageLoad) {
      setFirstPageLoad(false);
      return;
    }
    getGridData;
  }, [firstPageLoad, getGridData, loadDataOnInit]);

  const handlePageChange = (e: IPaginatorPageChange | undefined) => {
    if (!e) return;
    setFilter({
      ...filter,
      pageIndex: e.page,
      pageSize: e.rows,
    });
    if (filterSessionKey) setFilterStored(filter);
    setTriggerRefresh(true);
  };

  const renderContent = (
    <>
      <AvisFilter<F>
        filter={filter}
        filtered={!isEqual(filter, defaultFilterValues)}
        setFilter={setFilter}
        loading={isGridLoading}
        setRefresh={setTriggerRefresh}
        validationSchema={filterValidationSchema}
        renderForm={renderFilter}
      />
      <AvisFilterableTableGrid<T>
        initData={paginatedGridInfo}
        loading={isGridLoading}
        tableColumns={tableColumns}
        tableColumnWidths={tableColumnWidths}
        onPageChange={handlePageChange}
        customRowClassNames={customRowClassNames}
        conditionalFormatting={conditionalFormatting}
      />
      {renderFooter &&
        paginatedGridInfo &&
        renderFooter((paginatedGridInfo.items?.length || 0) > 0)}
    </>
  );
  if (!loadDataOnInit && firstPageLoad) return;

  return <div className="avis-filterable-table-container">{renderContent}</div>;
}
